import { IonCol, IonGrid, IonRow } from "@ionic/react";
import { useMemo } from "react";
import { useIntl } from "react-intl";

import styles from "@components/cards/Pin.module.css";
import Tag from "@components/labels/Tag";
import PinToggle from "@components/PinToggle";
import BodyCopy from "@components/typography/BodyCopy";
import InternalLink from "@components/typography/InternalLink";
import useMyClient from "@hooks/useMyClient";
import { GetPinsQuery, PinResourceTypeEnum } from "@typing/Generated";
import { journeyRoute } from "@utils/routeUtils";

type Props = {
  journeyId: string;
  noBorder?: boolean;
  pin: GetPinsQuery["pins"][0];
};

const Pin = ({ journeyId, noBorder = false, pin }: Props) => {
  const myClient = useMyClient();
  const intl = useIntl();

  const titleForPin = useMemo(() => {
    switch (pin.resource?.__typename) {
      case "Article":
        return pin.resource.articleTitle;
      case "EmployerBenefit":
        return pin.resource?.vendorName;
      case "Resource":
        return pin.resource?.resourceTitle;
      case "FileResource":
        return pin.resource?.fileResourceTitle;
      case "Worksheet":
        return pin.resource?.worksheetTitle;
      default:
        return "";
    }
  }, [pin.resource]);

  const resourceType = useMemo(() => {
    switch (pin.resource?.__typename) {
      case "Article":
        return PinResourceTypeEnum.ARTICLE;
      case "EmployerBenefit":
        return PinResourceTypeEnum.EMPLOYERBENEFIT;
      case "Resource":
        return PinResourceTypeEnum.RESOURCE;
      case "FileResource":
        return PinResourceTypeEnum.FILERESOURCE;
      case "Worksheet":
        return PinResourceTypeEnum.WORKSHEET;
      default:
        return undefined;
    }
  }, [pin.resource]);

  const link = useMemo(() => {
    switch (pin.resource?.__typename) {
      case "Article":
        return journeyRoute({
          journeyId,
          linkAfterJourney: `/articles/${pin.resource.id}`,
          myClient
        });
      case "EmployerBenefit":
        return journeyRoute({
          journeyId,
          linkAfterJourney: `/employer-benefits/${pin.resource.id}`,
          myClient
        });
      case "Resource":
        return journeyRoute({
          journeyId,
          linkAfterJourney: `/client-visible-tasks/${pin.resource.task.id}`,
          myClient
        });
      case "FileResource":
        return journeyRoute({
          journeyId,
          linkAfterJourney: `/drive`,
          myClient
        });
      case "Worksheet":
        return journeyRoute({
          journeyId,
          linkAfterJourney: `/worksheets/${pin.resource.id}`,
          myClient
        });
      default:
        return "";
    }
  }, [journeyId, myClient, pin.resource]);

  return (
    <div className={`${styles.pinContainer} ${noBorder ? styles.noBorder : ""}`}>
      <InternalLink linkEventCreateVariables={{ journeyId }} stylized={false} to={link}>
        <div className={styles.pin}>
          <IonGrid className="ion-no-padding">
            <IonRow className="ion-no-padding ion-align-items-center">
              {pin.resource?.__typename === "Article" && pin.resource.photoUrl && (
                <IonCol size="auto">
                  <img alt={pin.resource.articleTitle} className={styles.image} src={pin.resource.photoUrl} />
                </IonCol>
              )}
              <IonCol>
                <div className={styles.pinTitle}>
                  <BodyCopy bold color="primary">
                    {titleForPin}
                  </BodyCopy>
                </div>
              </IonCol>
              {pin.resource?.__typename !== "Article" && (
                <IonCol size="auto">
                  <Tag
                    color="gray-50"
                    text={intl.formatMessage({ id: `components.CarePlan.pins.${pin.resource?.__typename}` })}
                  />
                </IonCol>
              )}

              <IonCol size="auto">
                <div className="margin-4-left">
                  <PinToggle
                    color="primary"
                    journeyId={journeyId}
                    resourceId={pin.resource?.id}
                    resourceType={resourceType}
                    tool={pin.tool}
                  />
                </div>
              </IonCol>
            </IonRow>
          </IonGrid>
        </div>
      </InternalLink>
    </div>
  );
};

export default Pin;
